import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
  PLAYER_CLASS_PROFILES,
  type CapabilityMatrix as CapabilityMatrixData,
} from '@displaycms/contracts';

import { cn } from '@/lib/utils';

/**
 * P3 W2 B6 — co-active-version capability matrix (FS §6.2 / §8.5).
 *
 * PURE presentational unit (DS §3 — "tested by Vitest component, depends on
 * contracts"). It takes the server-computed `CapabilityMatrix` contract as a
 * prop and does NO data fetching. Rows = assigned players, columns = the
 * composer's co-active versions. Each cell answers "which version would this
 * player actually be served?" by comparing `player.resolved_version_id`
 * against the column's `composer_version_id` — this value is computed by
 * REUSING the live resolver path so it can never disagree with what
 * `resolveAssignment` serves. A player whose `resolved_version_id === null`
 * gets no compatible version (it will receive `assignment.unavailable`).
 *
 * Class labels come from the `PLAYER_CLASS_PROFILES` contract catalog
 * (NOT i18n); only the unknown-class fallback string is localized.
 */
export function CapabilityMatrix({
  data,
}: {
  data: CapabilityMatrixData;
}): JSX.Element {
  const { t } = useTranslation(['composers']);
  const lang: 'fr' | 'en' = i18next.language === 'en' ? 'en' : 'fr';

  return (
    <table
      data-testid="capability-matrix"
      className="w-full border-collapse text-left text-sm"
    >
      <thead>
        <tr className="border-b border-border">
          <th scope="col" className="px-3 py-2 font-medium">
            {t('composers:matrix.player_column')}
          </th>
          {data.versions.map((v) => (
            <th
              key={v.composer_version_id}
              scope="col"
              data-testid={`capability-matrix-version-${v.composer_version_id}`}
              className="px-3 py-2 font-medium"
            >
              <span>v{v.version_number}</span>
              {v.is_default_profile && (
                <span className="ml-1 text-xs text-muted-foreground">
                  {t('composers:matrix.default_tag')}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.players.map((p) => {
          const classLabel =
            p.player_class === null
              ? t('composers:matrix.unknown_class')
              : PLAYER_CLASS_PROFILES[p.player_class].label_i18n[lang];
          const unresolved = p.resolved_version_id === null;
          return (
            <tr
              key={p.player_id}
              data-testid={`capability-matrix-row-${p.player_id}`}
              className="border-b border-border last:border-0"
            >
              <th scope="row" className="px-3 py-2 font-normal text-left">
                <div className="flex flex-col">
                  <span className="font-medium">{p.display_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {classLabel}
                  </span>
                  {unresolved && (
                    <span
                      data-testid={`capability-matrix-none-${p.player_id}`}
                      className="mt-1 inline-flex w-fit items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900"
                    >
                      {t('composers:matrix.none')}
                    </span>
                  )}
                </div>
              </th>
              {data.versions.map((v) => {
                const plays =
                  p.resolved_version_id === v.composer_version_id;
                return (
                  <td
                    key={v.composer_version_id}
                    data-testid={`capability-matrix-cell-${p.player_id}-${v.composer_version_id}`}
                    className={cn(
                      'px-3 py-2 text-xs',
                      plays
                        ? 'font-medium text-green-900'
                        : 'text-muted-foreground',
                    )}
                  >
                    {plays
                      ? t('composers:matrix.will_play', {
                          v: v.version_number,
                        })
                      : t('composers:matrix.incompatible')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default CapabilityMatrix;
